import { useCallback, useEffect, useRef, useState } from 'react'
import type { FocusEvent, KeyboardEvent } from 'react'
import { useClickOutside } from '../../hooks'

export interface UseExpertisesDropdownParams {
    menubarItemsCount: number
    expertiseMenubarIndex: number
    isDesktopMenubar: boolean
    focusMenubarItemAt: (index: number) => void
}

export function useExpertisesDropdown({
    menubarItemsCount,
    expertiseMenubarIndex,
    isDesktopMenubar,
    focusMenubarItemAt,
}: UseExpertisesDropdownParams) {
    const [dropdownOpen, setDropdownOpen] = useState(false)

    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const submenuPanelRef = useRef<HTMLDivElement>(null)
    const focusExpertisesFirstAfterOpenRef = useRef(false)

    const expertiseTriggerTabIndex = dropdownOpen ? -1 : undefined

    const toggleExpertisesDropdownFromTrigger = useCallback(() => {
        setDropdownOpen((prev) => {
            if (prev) return false
            focusExpertisesFirstAfterOpenRef.current = true
            return true
        })
    }, [])

    const openDropdownDesktop = () => {
        if (window.innerWidth >= 1024) setDropdownOpen(true)
    }

    const closeDropdownDesktop = () => {
        if (window.innerWidth >= 1024) setDropdownOpen(false)
    }

    const closeDropdownAndFocusTrigger = useCallback(() => {
        setDropdownOpen(false)
        triggerRef.current?.focus()
    }, [])

    useEffect(() => {
        if (!dropdownOpen) return
        const onEscape = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                event.preventDefault()
                closeDropdownAndFocusTrigger()
            }
        }
        document.addEventListener('keydown', onEscape)
        return () => document.removeEventListener('keydown', onEscape)
    }, [dropdownOpen, closeDropdownAndFocusTrigger])

    const focusSubmenuLink = useCallback((index: number) => {
        const links =
            submenuPanelRef.current?.querySelectorAll<HTMLAnchorElement>(
                'a[role="menuitem"][href]'
            )
        if (links?.length) {
            const i = Math.max(0, Math.min(index, links.length - 1))
            links[i]?.focus()
        }
    }, [])

    useEffect(() => {
        if (!dropdownOpen) {
            focusExpertisesFirstAfterOpenRef.current = false
            return
        }
        if (!focusExpertisesFirstAfterOpenRef.current) return
        focusExpertisesFirstAfterOpenRef.current = false
        const id = requestAnimationFrame(() => focusSubmenuLink(0))
        return () => cancelAnimationFrame(id)
    }, [dropdownOpen, focusSubmenuLink])

    const handleExpertisesTriggerKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>) => {
            const count = menubarItemsCount
            const idx = expertiseMenubarIndex
            if (idx < 0 || count <= 0) return

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleExpertisesDropdownFromTrigger()
                return
            }
            if (e.key === 'Escape') {
                if (dropdownOpen) {
                    e.preventDefault()
                    setDropdownOpen(false)
                    queueMicrotask(() => triggerRef.current?.focus())
                }
                return
            }

            if (!dropdownOpen) {
                if (
                    (isDesktopMenubar && e.key === 'ArrowUp') ||
                    (!isDesktopMenubar && e.key === 'ArrowLeft')
                ) {
                    e.preventDefault()
                    setDropdownOpen(true)
                    queueMicrotask(() => {
                        const n =
                            submenuPanelRef.current?.querySelectorAll<HTMLAnchorElement>(
                                'a[role="menuitem"][href]'
                            )?.length ?? 0
                        if (n > 0) focusSubmenuLink(n - 1)
                    })
                    return
                }

                const nextKey = isDesktopMenubar ? 'ArrowRight' : 'ArrowDown'
                const prevKey = isDesktopMenubar ? 'ArrowLeft' : 'ArrowUp'

                if (e.key === nextKey) {
                    e.preventDefault()
                    focusMenubarItemAt(idx + 1)
                    return
                }
                if (e.key === prevKey) {
                    e.preventDefault()
                    focusMenubarItemAt(idx - 1)
                    return
                }
                if (e.key === 'Home') {
                    e.preventDefault()
                    focusMenubarItemAt(0)
                    return
                }
                if (e.key === 'End') {
                    e.preventDefault()
                    focusMenubarItemAt(count - 1)
                    return
                }
            }

            if (
                (isDesktopMenubar && e.key === 'ArrowDown') ||
                (!isDesktopMenubar && e.key === 'ArrowRight')
            ) {
                e.preventDefault()
                setDropdownOpen(true)
                queueMicrotask(() => focusSubmenuLink(0))
            }
        },
        [
            menubarItemsCount,
            expertiseMenubarIndex,
            isDesktopMenubar,
            dropdownOpen,
            toggleExpertisesDropdownFromTrigger,
            focusMenubarItemAt,
            focusSubmenuLink,
        ]
    )

    const handleSubmenuLinkKeyDown = useCallback(
        (e: KeyboardEvent<HTMLAnchorElement>, index: number) => {
            const count =
                submenuPanelRef.current?.querySelectorAll<HTMLAnchorElement>(
                    'a[role="menuitem"][href]'
                )?.length ?? 0
            if (e.key === 'Escape') {
                e.preventDefault()
                closeDropdownAndFocusTrigger()
            } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                if (index < count - 1) focusSubmenuLink(index + 1)
                else focusSubmenuLink(0)
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                if (index > 0) focusSubmenuLink(index - 1)
                else triggerRef.current?.focus()
            } else if (isDesktopMenubar && e.key === 'ArrowLeft') {
                e.preventDefault()
                closeDropdownAndFocusTrigger()
            } else if (e.key === 'Home') {
                e.preventDefault()
                focusSubmenuLink(0)
            } else if (e.key === 'End') {
                e.preventDefault()
                if (count > 0) focusSubmenuLink(count - 1)
            }
        },
        [closeDropdownAndFocusTrigger, focusSubmenuLink, isDesktopMenubar]
    )

    const handleDropdownKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') closeDropdownAndFocusTrigger()
    }

    const handleDropdownBlur = (e: FocusEvent<HTMLDivElement>) => {
        const related = e.relatedTarget as Node | null
        if (
            !related ||
            (!e.currentTarget.contains(related) &&
                !buttonRef.current?.contains(related))
        ) {
            setDropdownOpen(false)
        }
    }

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            window.innerWidth < 1024 &&
            !buttonRef.current?.contains(event.target as Node)
        ) {
            setDropdownOpen(false)
        }
    }, [])

    useClickOutside(dropdownRef, handleClickOutside)

    return {
        isDesktopMenubar,
        dropdownOpen,
        setDropdownOpen,
        dropdownRef,
        buttonRef,
        triggerRef,
        submenuPanelRef,
        expertiseTriggerTabIndex,
        toggleExpertisesDropdownFromTrigger,
        openDropdownDesktop,
        closeDropdownDesktop,
        handleExpertisesTriggerKeyDown,
        handleSubmenuLinkKeyDown,
        handleDropdownKeyDown,
        handleDropdownBlur,
    }
}
